<template>
  <li class="contribution">
    <app-avatar
      size="4"
      :avatar="contribution.customer.avatar"
    />

    <div class="contribution__infos">
      <div>
        <h3 class="contribution__name bold">
          <template v-if="contribution.customer.is_anonymous">
            {{ t('literal.anonymous') }}
          </template>

          <template v-else>
            {{ contribution.customer.fullname }}
          </template>
        </h3>

        <p>
          <app-money
            class="contribution__amount"
            :amount="amount"
            :default-currency="amount.currency"
            v-if="!amount.is_zero"
          />

          <app-date
            class="contribution__date color--lg"
            :timestamp="contribution.created_at"
          />
        </p>
      </div>

      <div class="contribution__actions">
        <template v-if="message && isModerator && !is_edited">
          <button
            class="contribution__button"
            :aria-label="t('literal.unmoderate_message')"
            @click.prevent="handleClickUnmoderateMessage()"
            v-if="isModerated"
          >
            <fa-icon
              aria-hidden="true"
              :icon="['fal', 'eye']"
              fixed-width
            />
          </button>

          <button
            class="contribution__button"
            :aria-label="t('literal.moderate_message')"
            @click.prevent="handleClickModerateMessage()"
            v-else
          >
            <fa-icon
              aria-hidden="true"
              :icon="['fal', 'eye-slash']"
              fixed-width
            />
          </button>
        </template>

        <template v-if="!isModerated && isContributor">
          <button
            class="contribution__button"
            :class="{ 'color--success': !loader }"
            :aria-label="t('literal.save')"
            @click.prevent="handleClickSave()"
            v-if="is_edited"
          >
            <app-spinner
              size="2"
              v-if="loader"
            />

            <fa-icon
              aria-hidden="true"
              :icon="['fal', 'save']"
              fixed-width
              v-else
            />
          </button>

          <button
            class="contribution__button"
            :aria-label="t('literal.edit')"
            @click.prevent="is_edited = true"
            v-else
          >
            <fa-icon
              aria-hidden="true"
              :icon="['fal', 'edit']"
              fixed-width
            />
          </button>
        </template>
      </div>

      <div class="contribution__message">
        <input-textarea
          class="textarea"
          v-model="message"
          v-if="is_edited && isContributor"
        />

        <template v-else-if="!is_edited">
          <p v-if="isModerated && isModerator">
            {{ t('public_contribution.moderated_message') }}
          </p>

          <p v-else-if="!isModerated">
            {{ message }}
          </p>
        </template>
      </div>
    </div>
  </li>
</template>

<script setup>
import { defineAsyncComponent, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from '@/vendors/i18n'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useProcedureExecutionStore } from '@/stores/ProcedureExecutionStore'
import { useRoles } from '@/composables/app/useRoles'

import AppDate from '&/atoms/AppDate'
import AppAvatar from '&/atoms/AppAvatar'
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))
const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))
const InputTextarea = defineAsyncComponent(() => import('&/atoms/InputTextarea'))

const emit = defineEmits(['update'])

const { contribution } = defineProps({
  project: { type: Object, default: () => ({}) },
  contribution: { type: Object, required: true }
})

const { t } = useI18n()
const { isKoalect, isSuperAdmin } = useRoles()
const { authUser } = storeToRefs(useAuthenticationStore())
const { patchPublicProcedureExecutionInformations } = useProcedureExecutionStore()

const loader = ref(false)
const is_edited = ref(false)
const cloned_message = ref(contribution.informations.message)

const isContributor = computed(() => authUser.value?.id === contribution.user_id)
const isModerator = computed(() => isKoalect.value || isSuperAdmin.value)
const isModerated = computed(() => /^(\*\*\/)[\s\S]+(\/\*\*)$/g.test(message.value))
const amount = computed(() => contribution.transaction?.amount)

const message = computed({
  get: () => cloned_message.value,
  set: value => cloned_message.value = value
})

const handleClickModerateMessage = () => {
  if (!message.value || isModerated.value) return

  message.value = '**/' + message.value + '/**'

  handleClickSave()
}

const handleClickUnmoderateMessage = () => {
  if (!message.value || !isModerated.value) return

  message.value = message.value.slice(3, -3)

  handleClickSave()
}

const handleClickSave = () => {
  loader.value = true

  return patchPublicProcedureExecutionInformations({ procedure_id: contribution.procedure_id, execution_id: contribution.id, params: { ...contribution.informations, message: message.value } })
    .then(() => {
      emit('update')

      is_edited.value = false
      loader.value = false
    })
}
</script>

<style lang="scss" scoped>
.contribution {
  display: grid;
  grid-template-columns: 48px auto;
  gap: 12px;

  &__infos {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 6px 0;
  }

  &__name {
    font-size: rem(14px);
  }

  &__amount {
    &::after {
      content: '';
      display: inline-block;
      vertical-align: middle;
      width: 4px;
      height: 4px;
      background: $lg;
      border-radius: 50%;
      margin: 0 6px;
    }
  }

  &__date {
    white-space: nowrap
  }

  &__button {
    padding: 3px;
    line-height: 16px;
  }

  &__message {
    word-wrap: break-word;
    grid-column: span 2
  }
}

.textarea :deep(.input) {
  background: transparent;
  border-radius: 5px;
  resize: vertical;
  min-height: 70px;
}
</style>
